// src/ConsumerApp/Consumer_app.js

import React, { useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthModalProvider } from './context/AuthModalContext';
import { ToastProvider } from './context/ToastContext';
import LoginModal from './Screens/Auth/LoginModal';

// ==========================================
// ACTIVE PRODUCTION SCREENS
// ==========================================
import HomeScreen from './Screens/Home/Home';
import SearchScreen from './Screens/Search/SearchScreen';
import ProductsList from './Screens/ProductsList/ProductsList';
import ProductDetailsScreen from './Screens/ProductDetails/ProductDetailsScreen';
import BucketScreen from './Screens/Bucket/Bucket';

// Account Management (Master-Detail Layout)
import AccountLayout from './Screens/Account/AccountLayout';
import OrdersMasterDetailView from './Screens/Account/views/OrdersMasterDetailView';
import ProfileContent from './Screens/Account/ProfileContent';
import SupportView from './Screens/Account/views/SupportView';
import OffersView from './Screens/Account/views/OffersView';
import WalletView from './Screens/Account/views/WalletView';
import PoliciesView from './Screens/Account/views/PoliciesView';
import InvoicesView from './Screens/Account/views/InvoicesView';
import SavedItemsView from './Screens/Account/views/SavedItemsView';
import RfqsView from './Screens/Account/views/RfqsView';

const consumerQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000 * 60 * 2,
    },
  },
});

export default function ConsumerApp() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Protected routes requiring an active user session
    const isProtected = location.pathname.startsWith('/consumer/account');
    const hasUser = Boolean(
      sessionStorage.getItem('userData') || localStorage.getItem('userData')
    );

    if (isProtected && !hasUser) {
      navigate('/consumer/HomeScreen', { replace: true });
    }
  }, [location.pathname, navigate]);

  return (
    <QueryClientProvider client={consumerQueryClient}>
      <ToastProvider>
      <AuthModalProvider>
        <Routes>
          {/* Default Redirect to Storefront */}
          <Route path="/" element={<Navigate to="/consumer/HomeScreen" replace />} />
          <Route path="/consumer" element={<Navigate to="/consumer/HomeScreen" replace />} />

          {/* Core Browsing & Commerce (Open to Guests) */}
          <Route path="/consumer/HomeScreen" element={<HomeScreen />} />
          <Route path="/consumer/search" element={<SearchScreen />} />
          <Route path="/consumer/productslist" element={<ProductsList />} />
          <Route path="/consumer/productdetails" element={<ProductDetailsScreen />} />
          <Route path="/consumer/bucket" element={<BucketScreen />} />

          {/* Account Sub-Routes (Protected Workspace) */}
          <Route path="/consumer/account" element={<AccountLayout />}>
            <Route index element={<Navigate to="orders" replace />} />
            <Route path="orders" element={<OrdersMasterDetailView />} />
            <Route path="orders/:orderId" element={<OrdersMasterDetailView />} />
            <Route path="profile" element={<ProfileContent />} />
            <Route path="support" element={<SupportView />} />
            <Route path="offers" element={<OffersView />} />
            <Route path="wallet" element={<WalletView />} />
            <Route path="policies" element={<PoliciesView />} />
            <Route path="invoices" element={<InvoicesView />} />
            <Route path="saved-items" element={<SavedItemsView />} />
            <Route path="rfqs" element={<RfqsView />} />
          </Route>

          {/* Account Shortcut */}
          <Route
            path="/consumer/AccountScreen"
            element={<Navigate to="/consumer/account/orders" replace />}
          />

          {/* Catch-all Fallback */}
          <Route path="*" element={<Navigate to="/consumer/HomeScreen" replace />} />
        </Routes>

        {/* Global Modal Layer */}
        <LoginModal />
      </AuthModalProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}